/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useRef, useEffect, useMemo, useCallback, type RefObject } from 'react'
import { useWindowSize } from '@uidotdev/usehooks';
import '@/styles/App.css';
import { saveTasks, getTasks, calculate, processIntent, type Task, type Filter as NodeFilter, type CommitEvent } from './Tasks.ts';
import { Inspect, Tooltip, ListView} from './Inspect.tsx';

import { onboardingTasks } from './data.js';
import { Button } from './components/ui/button.tsx';
import { Input } from './components/ui/input.tsx';
import { Label } from './components/ui/label.tsx';
import { CloudDownload, Tag, UserIcon, LogOutIcon, ChevronDown, Undo2, Redo2, Save, Filter, CircleIcon, SquareIcon, Cog, XIcon, CheckCheck, RefreshCw, CalendarRange } from 'lucide-react';
import { Simulation, COLORS, type Config} from './Sim.ts';

import { 
  DropdownMenu, 
  DropdownMenuCheckboxItem, 
  DropdownMenuContent, DropdownMenuGroup, 
  DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuItem, DropdownMenuLabel, 
  DropdownMenuSubContent,
  DropdownMenuSub,
  DropdownMenuSubTrigger
} from '@/components/ui/dropdown-menu.tsx';
import { supabase } from './lib/supabase/client.ts';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from './components/ui/resizable.tsx';
import { usePanelRef } from 'react-resizable-panels';

type SpawnHint = {
  id: string,
  x: number,
  y: number
}

import { type TaskMap } from './Tasks.ts';

/*
 
 ███████╗██╗███╗   ███╗     ██████╗ ██████╗ ███╗   ███╗██████╗  ██████╗ ███╗   ██╗███████╗███╗   ██╗████████╗
 ██╔════╝██║████╗ ████║    ██╔════╝██╔═══██╗████╗ ████║██╔══██╗██╔═══██╗████╗  ██║██╔════╝████╗  ██║╚══██╔══╝
 ███████╗██║██╔████╔██║    ██║     ██║   ██║██╔████╔██║██████╔╝██║   ██║██╔██╗ ██║█████╗  ██╔██╗ ██║   ██║   
 ╚════██║██║██║╚██╔╝██║    ██║     ██║   ██║██║╚██╔╝██║██╔═══╝ ██║   ██║██║╚██╗██║██╔══╝  ██║╚██╗██║   ██║   
 ███████║██║██║ ╚═╝ ██║    ╚██████╗╚██████╔╝██║ ╚═╝ ██║██║     ╚██████╔╝██║ ╚████║███████╗██║ ╚████║   ██║   
 ╚══════╝╚═╝╚═╝     ╚═╝     ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚═╝  ╚═══╝   ╚═╝   
                                                                                                             
 
*/
export function Sim({ tasks, onCommit, selectTask, hoverTask, selectedTask, addDependencyTask, setAddDependencyTask, vizConfig, simRef } :
  { tasks: TaskMap,
    onCommit: (events: CommitEvent[]) => void,
    selectTask: (id: string) => void,
    hoverTask: (id: string) => void,
    selectedTask: string,
    addDependencyTask: string,
    setAddDependencyTask: (id:string) => void,
    vizConfig: Config,
    simRef?: RefObject<Simulation | null>
  } ) {

  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef(null);

  const solvedTasks = useMemo( () => calculate(tasks), [tasks])
  const localSimRef = useRef<Simulation | null>(null);
  const sim = simRef ?? localSimRef;


  // TODO3: Fix height & width
  const HEIGHT = 800;
  //const width = 500;

  useEffect(() => {  
    sim.current = new Simulation(svgRef.current,
      containerRef.current,
      selectTask,
      onCommit,
      setAddDependencyTask,
      hoverTask,
      vizConfig
    );

    console.debug('init effect');

    sim.current.init();

  }, [selectTask, setAddDependencyTask, hoverTask, onCommit]);
// TODO figure out why onCommit breaks this effect


  useEffect(() => {
    console.debug("Task update effect", solvedTasks);
    sim.current.update(solvedTasks);
  }, [solvedTasks, tasks]);



  useEffect(() => {

    if (!addDependencyTask) {
      // Do cleanup
      console.debug("addDependencyTask was set to", addDependencyTask, "so we are cleaning up");
      sim.current.addDependencyResume();

      return
    }

    sim.current.addDependency(addDependencyTask)

  }, [addDependencyTask])

  useEffect(() => { // on selectedTask change
    console.log("Selected task is now", selectedTask);
    sim.current.setSelectedTask(selectedTask);
  }, [selectedTask]);

  useEffect(() => { // on viz settings change
    sim.current.setConfig(vizConfig)
  }, [vizConfig])


  return (
    <div style={{border:'0px dotted blue'}} id='svg-container' ref={containerRef}>
      <svg ref={svgRef} width='100%' > </svg>
    </div>
  )
}

/*
 
  █████╗ ██████╗ ██████╗ 
 ██╔══██╗██╔══██╗██╔══██╗
 ███████║██████╔╝██████╔╝
 ██╔══██║██╔═══╝ ██╔═══╝ 
 ██║  ██║██║     ██║     
 ╚═╝  ╚═╝╚═╝     ╚═╝     
                         
 
*/

export function FilterSettings({
  groupTitle=(<><Cog/>Filter group</>),
  subTitle=(<>Apply property to:</>),
  initialState,
  updateCallback
} : {
  groupTitle?, subTitle?,
  initialState: NodeFilter
  updateCallback: (arg0: NodeFilter) => void
}) {
  // this is an 'any'/'or' gate

  const [state, setState] = useState(initialState);

  function propagate(state : NodeFilter) {
    setState(state)
    updateCallback(state)
  }

  // TODO: improve "none" behavior. Should update the other checkboxes when used.


  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{groupTitle}</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuLabel>{subTitle}</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={state.blocked}
            onCheckedChange={() => propagate({...state, blocked: !state.blocked})}
          >
            <CircleIcon fill={COLORS.node.fillBlocked}/>
            Blocked
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={state.complete}
            onCheckedChange={() => propagate({...state, complete: !state.complete})}
          >
            <CircleIcon fill={COLORS.node.fillComplete}/>
            Complete
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={state.external}
            onCheckedChange={() => propagate({...state, external: !state.external})}
          >
            <SquareIcon />
            External
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={state.resolved}
            onCheckedChange={() => propagate({...state, resolved: !state.resolved})}
          >
            <CheckCheck />
            Resolved
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={state.rejectAll}
            onCheckedChange={b => propagate({...state, rejectAll: b})}
          >
            <XIcon stroke='red'/>
            None
          </DropdownMenuCheckboxItem>
        </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}


export default function App({user}) {

  const [tasks, setTasks] = useState<TaskMap>(() => user == 'sandbox' ? onboardingTasks['snapshot'] : {})
  const solvedTasks = useMemo( () => calculate(tasks), [tasks])

  const panelRef = usePanelRef();
  const vizPanelRef = usePanelRef();
  const simRef = useRef<Simulation | null>(null);

  const [selectedTaskID, setSelectedTaskID] = useState<string>(() => user == 'sandbox' ? 'x3' : undefined);
  const [hoveredTaskID, setHoveredTaskID] = useState<string>();
  const [addDependencyTaskID, setAddDependencyTaskID] = useState<string>();

  const { width } = useWindowSize();
  const isWide = width >= 900;

  const save = () => {
    if (user == 'sandbox') { console.log(solvedTasks); return; }
    saveTasks(solvedTasks)
      .then(() => toast.success('Uploaded successfully'))
      .catch(() => toast.error('Upload failed'));
  };
  const load = () => {
    if (user == 'sandbox') { setTasks(onboardingTasks['snapshot']); return; }
    getTasks()
      .then(data => { setTasks(data); toast.success('Downloaded successfully'); })
      .catch(() => toast.error('Download failed'));
  };

  //const load = () => setTasks(getTasks())

  const [vizConfig, setVizConfig] = useState<Config>({
    labelFilter: {complete: true, blocked: true, external: true, resolved: true},
    taskFilter: {complete: true, blocked: true, external: true, resolved: false},
    dateLabelFormat: 'OFFSET',
    futureDays: 10
  })
  //const getVizConfig = () => 

  //console.log('App component reload')
  
  //console.log("Initial task import:", tasks)

  function handleCommit(event: CommitEvent) {
    //setTasks(prev =>)
    console.log("Commit event:", event);

    setTasks(processIntent(event, solvedTasks))

    //console.debug("Updated tasks: ", tasks);
  }

  const handleCommits = useCallback((events : CommitEvent[]) => {

    console.log("Handle commits:", events);

    setTasks(prev => {
      let updated = calculate(prev);
      for (const e of events) {
        updated = processIntent(e, updated);
      }
      return updated;
    })

  }, [])

  const selectTask = useCallback((taskID) => {
    setSelectedTaskID(taskID);
  }, [])

  const [expanded, setExpanded] = useState<boolean>(false)

  useEffect(() => {
    if (user !== 'sandbox') {
      getTasks().then(data => setTasks(data))
    }
  }, [user])

  useEffect(() => {
    if (selectedTaskID && panelRef.current.getSize().asPercentage < 15) {
      panelRef.current.resize("30%")
    }
  }, [selectedTaskID, panelRef])

  const ButtonBar =  (
    <div className='buttonbar'>
      {/* <Button onClick={save}><BsFillCloudUploadFill />Upload</Button> */}
      {/* <Button onClick={load}> <BsFillCloudDownloadFill /> Download</Button> */}
      <a href='/' className='topleft text-2xl font-bold px-4' style={{fontVariant: 'small-caps'}}>Zeno</a>

      <span className='spacer'></span>
      <div className='future-days-input flex items-center gap-2'>
        <Label htmlFor='future-days'><CalendarRange size={16} />Days:</Label>
        <Input id='future-days' type='number' min={1} className='w-20'
          value={vizConfig.futureDays}
          onChange={e => {
            const n = parseInt(e.target.value, 10);
            if (!isNaN(n) && n > 0) { setVizConfig({...vizConfig, futureDays: n}) }
          }}
        />
      </div>
      <Button variant="outline" onClick={() => simRef.current?.reheat()}>
        <RefreshCw />
        Reheat
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <UserIcon />
            {user}
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='w-60 l-5'>
          <DropdownMenuGroup>
            <DropdownMenuLabel>Settings</DropdownMenuLabel>

            <FilterSettings 
              groupTitle={<><Tag /> Show labels</>}
              subTitle={<>Show labels for:</>}
              initialState={vizConfig.labelFilter}
              updateCallback={f => setVizConfig({...vizConfig, labelFilter: f})}
            />

            <FilterSettings
              groupTitle={<><Filter /> Filter tasks</>}
              subTitle={<>Show tasks:</>}
              initialState={vizConfig.taskFilter}
              updateCallback={f => setVizConfig({...vizConfig, taskFilter: f})}
            />

            <DropdownMenuItem disabled>
              <Undo2 />Undo
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Redo2/>Redo
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuLabel>Sync</DropdownMenuLabel>
            <DropdownMenuItem onClick={save}>
              <Save stroke='black'/>
              Upload
            </DropdownMenuItem>
            <DropdownMenuItem onClick={load}>
              <CloudDownload stroke='black'/>
              Download
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </DropdownMenuGroup>

        <DropdownMenuGroup>
          <DropdownMenuLabel>Account</DropdownMenuLabel>
          <DropdownMenuItem variant='destructive'
            onClick={e => supabase.auth.signOut({scope: 'global'})}>
            <LogOutIcon />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

    </div>
  )

  return (
    <>

      { ButtonBar }
      <ResizablePanelGroup orientation={isWide ? 'horizontal' : 'vertical'}>
        {isWide && <>
          <ResizablePanel defaultSize="25%" panelRef={panelRef}>
            <Inspect tasks={solvedTasks} selectTask={selectTask}
              taskID={selectedTaskID}
              onCommit={handleCommit}
              addDependencyTask={addDependencyTaskID}
              setAddDependencyTask={setAddDependencyTaskID}
              simRef={simRef}
            />
            <div id='list-container' style={{display: selectedTaskID ? 'none' : 'block'}}>
              <ListView tasks={solvedTasks} selectTask={selectTask}
                onCommit={handleCommit} hoveredTaskID={hoveredTaskID}/>
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle/>
        </>}
        <ResizablePanel style={{height: '100%', overflowY: 'scroll'}} panelRef={vizPanelRef} id='upper-pane'>
          <Sim tasks={solvedTasks} selectTask={selectTask}
            hoverTask={setHoveredTaskID}
            onCommit={handleCommits}
            selectedTask={selectedTaskID}
            addDependencyTask={addDependencyTaskID}
            setAddDependencyTask={setAddDependencyTaskID}
            vizConfig={vizConfig}
            simRef={simRef}
          />
        </ResizablePanel>
        {!isWide && <>
          <ResizableHandle withHandle/>
          <ResizablePanel defaultSize="25%" panelRef={panelRef}>
            <Inspect tasks={solvedTasks} selectTask={selectTask}
              taskID={selectedTaskID}
              onCommit={handleCommit}
              addDependencyTask={addDependencyTaskID}
              setAddDependencyTask={setAddDependencyTaskID}
              simRef={simRef}
            />
            <div id='list-container' style={{display: selectedTaskID ? 'none' : 'block'}}>
              <ListView tasks={solvedTasks} selectTask={selectTask}
                onCommit={handleCommit} hoveredTaskID={hoveredTaskID}/>
            </div>
          </ResizablePanel>
        </>}
      </ResizablePanelGroup>

      <Tooltip tasks={tasks} taskID={hoveredTaskID}/>
      <div id='debug'>{addDependencyTaskID}
      <p className="alpha"></p></div>
      <Toaster />
    </>
  )

}

//export default App
